import { getFullAwsServiceName } from 'shared/utils/awsUtils';
import {
  CostUsageStates,
  mapCloudBaseCostUsageStateToAttributesMap,
  mapCloudBaseGroupByToCostUsageStateMap,
  mapCostUsageStateToAttributes,
} from 'usage/constants/costAndUsageConstants';
import { AWS_QUANTITY_TYPE_SELECT, AwsCommonFields, AwsQuantityTypes } from 'shared/constants/awsConstants';
import { CLOUD_TYPE_IDS } from 'users/constants/usersConstants';
import { getDisplayKey } from '~/shared/utils/dataPrepareUtil.js';

// sync mappings with app-server
export const adjustK8SResourceTypeValue = (value, reversed = false) => {
  const mapping = {
    cpu: 'CPU',
    memory: 'Memory',
    storage: 'Storage',
    network_tx: 'Network TX (outgoing)',
    network_rx: 'Network RX (incoming)',
  };
  if (!reversed) {
    return mapping[value] || value;
  }
  const reverse = Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k]));
  return reverse[value] || value;
};

const adjustK8SAllocationStatus = (value, reversed = false) => {
  const mapping = {
    actual_usage: 'Actual Usage',
    unused_reserved: 'Pod Level Waste',
    unallocated: 'Node Level Waste',
    data_unavailable: 'Not Available',
  };
  if (!reversed) {
    return mapping[value] || value;
  }
  const reverse = Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k]));
  return reverse[value] || value;
};

export default class BaseCostUsageDataState {
  constructor(context) {
    this.data = null;
    this.context = context;
  }

  getData = () => {
    const stateType = this.getDataStateType();
    this.data = this.context.props.invoiceStore.dataStates[stateType];
    return this.data;
  };

  getSupportedQuantityTypes = (cloudTypeId = CLOUD_TYPE_IDS.AWS) => {
    if (cloudTypeId === CLOUD_TYPE_IDS.GCP) {
      return [
        AWS_QUANTITY_TYPE_SELECT,
        AwsQuantityTypes.HOURS,
        AwsQuantityTypes.BYTES,
        AwsQuantityTypes.OTHER,
        AwsQuantityTypes.QUANTITY,
      ];
    }
    return [
      AWS_QUANTITY_TYPE_SELECT,
      AwsQuantityTypes.HOURS,
      AwsQuantityTypes.REQUESTS,
      AwsQuantityTypes.BYTES,
      AwsQuantityTypes.OTHER,
      AwsQuantityTypes.QUANTITY,
    ];
  };

  modifyDataKey = (dataKey, currGroupBy) => {
    if (currGroupBy === AwsCommonFields.PAYER_ACCOUNT) {
      return dataKey.split(' (')[1].replace(')', '');
    }
    if (currGroupBy === AwsCommonFields.K8S_RESOURCE_TYPE) {
      return adjustK8SResourceTypeValue(dataKey, true);
    }
    if (currGroupBy === AwsCommonFields.K8S_ALLOCATION_STATUS) {
      return adjustK8SAllocationStatus(dataKey, true);
    }
    return dataKey;
  };

  getBreadCrumbLabel = (value, groupBy = '') => {
    if (groupBy === AwsCommonFields.K8S_RESOURCE_TYPE) {
      return adjustK8SResourceTypeValue(value);
    }
    if (groupBy === AwsCommonFields.K8S_ALLOCATION_STATUS) {
      return adjustK8SAllocationStatus(value);
    }
    return getDisplayKey(value, groupBy);
  };

  getNextDrillDataState = (modifiedDataKey) => {
    const { usersStore } = this.context.props;
    const { nextDrillDown } = this.context.state;
    if (nextDrillDown) {
      const map = mapCloudBaseGroupByToCostUsageStateMap.get(usersStore.currDispUserCloudAccountType);
      return map.get(nextDrillDown);
    }
    return this.getNewDataStateType(modifiedDataKey);
  };

  handleDive = (date, dataKey, cloudTypeId) => {
    const { usersStore } = this.context.props;
    const { currentGroupBy, dataKeyToWhereParamsMap } = this.context.state;
    const modifiedDataKey = this.modifyDataKey(dataKey, currentGroupBy);
    const newDataState = this.getNextDrillDataState(modifiedDataKey);
    const newGroupBy =
      newDataState === CostUsageStates.WORKLOAD
        ? `${AwsCommonFields.WORKLOAD}:${modifiedDataKey}`
        : mapCostUsageStateToAttributes.get(newDataState);
    let currentGroupByFormatted = currentGroupBy;
    if (currentGroupBy?.startsWith(AwsCommonFields.WORKLOAD)) {
      [currentGroupByFormatted] = currentGroupBy.split(':');
    }
    if (newDataState === CostUsageStates.RESOURCE && cloudTypeId === CLOUD_TYPE_IDS.MULTI) {
      return;
    }
    let nextDrillDown = null;
    const nextDefaultDrill = this.context.dataStates[newDataState]?.getNewDataStateType();
    let nextGroupBy = mapCostUsageStateToAttributes.get(nextDefaultDrill);
    const newDataKeyToWhereParamsMap = {
      ...dataKeyToWhereParamsMap,
      [currentGroupByFormatted]: getFullAwsServiceName(modifiedDataKey),
    };
    // save check to avoid infinite loop
    let iterations = 0;
    while (newDataKeyToWhereParamsMap[nextGroupBy] && iterations < 10) {
      const nextDataState = mapCloudBaseGroupByToCostUsageStateMap
        .get(usersStore.currDispUserCloudAccountType)
        .get(nextGroupBy);
      nextGroupBy = mapCostUsageStateToAttributes.get(this.context.dataStates[nextDataState]?.getNewDataStateType());
      nextDrillDown = nextGroupBy;
      iterations++;
    }
    this.context.setState({
      nextDrillDown,
      diveDate: date,
      isApplyFiltersButtonDisabled: true,
      currDataState: newDataState,
      currentGroupBy: newGroupBy,
      dataKeyToWhereParamsMap: newDataKeyToWhereParamsMap,
    });
  };

  handleBackTo = () => {
    const dataStateType = this.getDataStateType();
    const { dataKeyToWhereParamsMap } = this.context.state;
    const newGroupBy = this.getCurrentGroupByFromWhereParams(mapCostUsageStateToAttributes.get(dataStateType));
    const keys = [...Object.keys(dataKeyToWhereParamsMap)];
    const newDataKeyToWhereParamsMap = {};

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === newGroupBy) {
        break;
      }
      newDataKeyToWhereParamsMap[keys[i]] = dataKeyToWhereParamsMap[keys[i]];
    }
    // clear previous data state
    this.context.props.invoiceStore.clearDataState(this.context.state.currDataState);
    this.context.setState({
      currDataState: dataStateType,
      currentGroupBy: newGroupBy,
      dataKeyToWhereParamsMap: newDataKeyToWhereParamsMap,
    });
  };

  getCurrentGroupByFromWhereParams = (dataStateAttr) => {
    if ((dataStateAttr || '').includes('customtags') || (dataStateAttr || '').includes(AwsCommonFields.ACCOUNT_TAGS)) {
      const { dataKeyToWhereParamsMap } = this.context.state;
      const wheresKeys = Object.keys(dataKeyToWhereParamsMap);
      const fulldataStateAttr = wheresKeys.find((whereKey) => whereKey.split(':')[0] === dataStateAttr);
      return fulldataStateAttr || dataStateAttr;
    }
    if ((dataStateAttr || '').startsWith(AwsCommonFields.WORKLOAD)) {
      const { dataKeyToWhereParamsMap } = this.context.state;
      return `${dataStateAttr}:${dataKeyToWhereParamsMap[AwsCommonFields.WORKLOAD_TYPE]}`;
    }
    return dataStateAttr;
  };

  handleFilterChange = async (
    groupByFilter,
    dataKeyToWhereParamsMap,
    startDate,
    endDate,
    currPeriodGranLevel,
    filters,
    currCostType,
    isShowAmortize,
    isApplyMargin,
    isNetAmortize,
    isNetUnblended,
    isPublicCost,
    isDistributed,
    isListUnitPrice,
    isSavingsCost,
  ) => {
    const stateType = this.getDataStateType();
    const { filterBarGroupBySecondary } = this.context.state;
    this.context.updateDataDates();
    await this.context.props.invoiceStore.fetchDataByState(
      stateType,
      groupByFilter,
      filterBarGroupBySecondary,
      groupByFilter,
      dataKeyToWhereParamsMap,
      startDate,
      endDate,
      currPeriodGranLevel,
      filters,
      currCostType,
      isShowAmortize,
      isApplyMargin,
      isNetAmortize,
      isNetUnblended,
      isPublicCost,
      isDistributed,
      isListUnitPrice,
      isSavingsCost,
    );
  };

  getPreparedNewGroupBy = (newGroupBy) => {
    let preparedNewGroupBy = '';
    switch (true) {
      case newGroupBy.split(':')[0] === 'customtags':
        preparedNewGroupBy = 'customtags';
        break;
      case newGroupBy.split(':')[0] === 'accounttags':
        preparedNewGroupBy = 'accounttags';
        break;
      case newGroupBy.split(':')[0] === 'virtualcustomtags':
        preparedNewGroupBy = 'virtualcustomtags';
        break;
      case newGroupBy.split(':')[0] === 'viewscustomtags':
        preparedNewGroupBy = 'viewscustomtags';
        break;
      case newGroupBy.split(':')[0] === AwsCommonFields.K8S_LABEL_TAGS:
        preparedNewGroupBy = AwsCommonFields.K8S_LABEL_TAGS;
        break;
      case newGroupBy.split(':')[0] === AwsCommonFields.K8S_NODE_LABEL:
        preparedNewGroupBy = AwsCommonFields.K8S_NODE_LABEL;
        break;
      case newGroupBy.split(':')[0] === AwsCommonFields.WORKLOAD:
        preparedNewGroupBy = AwsCommonFields.WORKLOAD;
        break;
      case newGroupBy.split(':')[0] === AwsCommonFields.BUSINESS_MAPPING_VIEWPOINTS:
        preparedNewGroupBy = AwsCommonFields.BUSINESS_MAPPING_VIEWPOINTS;
        break;
      default:
        preparedNewGroupBy = newGroupBy;
        break;
    }
    return preparedNewGroupBy;
  };

  handleGroupBy = async (newGroupBy, newSecondaryGroupBy, newDataState) => {
    // if GroupBy is the same - No required change
    const { filterBarGroupBySecondary, filterBarGroupBy } = this.context.state;
    if (newGroupBy === filterBarGroupBy && newSecondaryGroupBy === filterBarGroupBySecondary) {
      return;
    }
    await this.fetchDataByState(newDataState, newSecondaryGroupBy, newGroupBy);
  };

  fetchDataByState = async (dataState, newSecondaryGroupBy = '', newGroupBy) => {
    const {
      excludedFiltersStatusMap,
      likeFiltersStatus,
      isShowAmortizeCost,
      isNetAmortize,
      isNetUnblended,
      isPublicCost,
      isDistributed,
      isListUnitPrice,
      isSavingsCost,
      currCostType,
      fieldToFilterdValuesMap,
      endDate,
      selectedGranLevel,
      isApplyMargin,
      startDate,
      isRateUsageBased,
      carbonEmissionsUsage,
    } = this.context.state;
    const { currDispUserCloudAccountType } = this.context.props.usersStore;
    const mapCloudBase = mapCloudBaseCostUsageStateToAttributesMap.get(currDispUserCloudAccountType);
    const stateAttribute = dataState === CostUsageStates.SECOND_GROUP_BY ? newGroupBy : mapCloudBase.get(dataState);
    this.context.updateDataDates();
    await this.context.props.invoiceStore.fetchDataByState(
      dataState,
      stateAttribute, // groupByLevel
      newSecondaryGroupBy,
      newGroupBy || stateAttribute,
      {},
      startDate,
      endDate,
      selectedGranLevel,
      {
        isRateUsageBased,
        carbonEmissionsUsage,
        filtersMap: fieldToFilterdValuesMap,
        excludedFiltersStatusMap,
        likedFiltersStatus: likeFiltersStatus,
      },
      currCostType,
      isShowAmortizeCost,
      isApplyMargin,
      isNetAmortize,
      isNetUnblended,
      isPublicCost,
      isDistributed,
      isListUnitPrice,
      isSavingsCost,
    );
  };
}
